import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import Slider, { SliderThumb } from "@mui/material/Slider";

type RangeSliderProps = {
  range: number[];
  title: string;
  max: number;
  min: number;
  setRange: (range: number[]) => void;
  histogramValues: number[];
};

// Defined outside the component so its identity is stable across renders.
// If defined inside, every re-render produces a new function reference,
// causing MUI to unmount/remount the thumb DOM node mid-drag, which kills
// the pointer-capture and makes the thumb unresponsive on touch devices.
function AirbnbThumbComponent(props: React.HTMLAttributes<unknown>) {
  const { children, ...other } = props;
  return (
    <SliderThumb {...other}>
      {children}
      <span className="airbnb-bar" />
      <span className="airbnb-bar" />
      <span className="airbnb-bar" />
    </SliderThumb>
  );
}

const generateHistogram = (
  histogramValues: number[],
  _min: number,
  _max: number,
  bins: number
) => {
  const values = histogramValues?.filter((v) => v > 0);
  if (!values?.length) return new Array(bins).fill(0);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  if (dataMin === dataMax) return new Array(bins).fill(1);
  const binSize = (dataMax - dataMin) / bins;
  const histogram = new Array(bins).fill(0);
  values.forEach((v) => {
    const idx = Math.floor((v - dataMin) / binSize);
    histogram[Math.min(idx, bins - 1)] += 1;
  });
  return histogram;
};

const RangeSlider = ({
  range,
  title,
  max,
  min,
  setRange,
  histogramValues,
}: RangeSliderProps) => {
  const [histogram, setHistogram] = useState<number[]>([]);
  const [range1, setRange1] = useState(range);
  const [inputMin, setInputMin] = useState(String(range[0]));
  const [inputMax, setInputMax] = useState(String(range[1]));

  useEffect(() => {
    setHistogram(generateHistogram(histogramValues, min, max, 25));
  }, [histogramValues, min, max]);

  useEffect(() => {
    setRange1(range);
  }, [range]);

  useEffect(() => {
    setInputMin(String(range1[0]));
    setInputMax(String(range1[1]));
  }, [range1]);

  const handleChange = (_event: Event, newValue: number | number[]) => {
    setRange1(newValue as number[]);
  };

  const handleChangeCommitted = (_event: any, newValue: number | number[]) => {
    setRange(newValue as number[]);
  };

  const handleMinBlur = () => {
    const parsed = parseInt(inputMin);
    const val = isNaN(parsed)
      ? min
      : Math.max(min, Math.min(parsed, range1[1] - 1));
    setRange1([val, range1[1]]);
    setRange([val, range1[1]]);
  };

  const handleMaxBlur = () => {
    const parsed = parseInt(inputMax);
    const val = isNaN(parsed)
      ? max
      : Math.min(max, Math.max(parsed, range1[0] + 1));
    setRange1([range1[0], val]);
    setRange([range1[0], val]);
  };

  const maxHistogramValue = Math.max(...histogram, 1);

  // Compute data range for bar-to-price mapping (active coloring)
  const dataValues = (histogramValues || []).filter((v) => v > 0);
  const dataMin = dataValues.length > 0 ? Math.min(...dataValues) : min;
  const dataMax = dataValues.length > 0 ? Math.max(...dataValues) : max;
  const barStep = histogram.length > 0 ? (dataMax - dataMin) / histogram.length : 1;

  return (
    <Box pt={2}>
      <Box position="relative" mx={2} mt={3} height={50}>
        {/* Histogram bars */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            bottom: 31,
            display: "flex",
            alignItems: "flex-end",
            width: "100%",
          }}
        >
          {histogram.map((bar, index) => {
            const barMin = dataMin + index * barStep;
            const barMax = dataMin + (index + 1) * barStep;
            const isActive = barMax > range1[0] && barMin < range1[1];
            return (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: `${(bar / maxHistogramValue) * 50}px`,
                  backgroundColor: isActive ? "#333" : "#e0e0e0",
                  marginRight: "2px",
                  borderTopLeftRadius: "2px",
                  borderTopRightRadius: "2px",
                  transition: "background-color 0.08s",
                }}
              />
            );
          })}
        </Box>

        <Slider
          sx={{
            color: "#000",
            height: "2px",
            padding: "20px 0",
            position: "relative",
            zIndex: 2,
            "& .MuiSlider-thumb": {
              height: "30px",
              width: "30px",
              backgroundColor: "#fff",
              border: "1px solid #b7b7b7",
              "&:hover": {
                boxShadow: "none",
              },
              "& .airbnb-bar": {
                height: "12px",
                width: "1px",
                backgroundColor: "#b7b7b7",
                marginLeft: "1px",
                marginRight: "1px",
              },
            },
            "& .MuiSlider-track": {
              height: "1px",
            },
            "& .MuiSlider-rail": {
              color: "#dbdbdb",
              opacity: "1",
              height: "2px",
            },
          }}
          value={range1}
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
          getAriaValueText={(value) => `${value}`}
          aria-labelledby="track-inverted-range-slider"
          min={min}
          max={max}
          slots={{ thumb: AirbnbThumbComponent }}
        />
      </Box>

      {/* Editable min/max input boxes */}
      <Box sx={{ display: "flex", gap: "8px", mt: 2, mx: 1 }}>
        <Box
          sx={{
            flex: 1,
            border: "1px solid #b7b7b7",
            borderRadius: "8px",
            p: "8px 12px",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Minimum
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {title === "Price" && (
              <Typography variant="body2">€</Typography>
            )}
            <input
              value={inputMin}
              onChange={(e) => setInputMin(e.target.value)}
              onBlur={handleMinBlur}
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                fontSize: "14px",
                background: "transparent",
              }}
            />
            {title === "Length" && (
              <Typography variant="body2">ft</Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            border: "1px solid #b7b7b7",
            borderRadius: "8px",
            p: "8px 12px",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Maximum
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {title === "Price" && (
              <Typography variant="body2">€</Typography>
            )}
            <input
              value={inputMax}
              onChange={(e) => setInputMax(e.target.value)}
              onBlur={handleMaxBlur}
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                fontSize: "14px",
                background: "transparent",
              }}
            />
            {title === "Length" && (
              <Typography variant="body2">ft</Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RangeSlider;
